import { useCallback } from "react";

import { inspectVideoPreviewMetadata } from "../../../api";
import { extractExifData } from "../../../utils/exifUtils";
import {
  CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
  CATCH_IMAGE_PREVIEW_ERROR_CODE,
  CATCH_IMAGE_PREVIEW_ERROR_MESSAGE,
  CATCH_IMAGE_PREVIEW_UNAVAILABLE_MESSAGE,
  buildCatchMediaClientError,
  buildUploadFilename,
  getFilenameExtension,
  inferSelectedMediaType,
  isDirectCatchImageFormatSafe,
  normalizeMimeType,
  requiresClientImagePreparation,
} from "./catchMediaConstants";
import {
  buildPreviewSource,
  normalizeImageFileForCatchMedia,
  readFileAsDataUrl,
} from "./catchMediaPreview";
import { SUBMIT_PHASES, computeSubmitReady } from "./catchSubmitUtils";

function extractVideoCapturedAtPreviewParts(capturedAtInput) {
  if (typeof capturedAtInput !== "string") {
    return null;
  }

  const normalized = capturedAtInput.trim();
  if (!normalized) {
    return null;
  }

  const match = normalized.match(
    /^(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/i
  );

  if (!match) {
    return null;
  }

  const [, datePart, timePart] = match;
  return {
    date: datePart || null,
    time: timePart || null,
  };
}

export default function useCatchMediaApplyFlow({
  selectionIdRef,
  uploadAssetRef,
  setMediaStateForSelection,
  updateMediaState,
  clearDeferredFileInput,
  rejectImageSelectionWithoutSnapshot,
  setSubmitPhase,
  applyDetectedExifDate,
  applyDetectedExifTime,
  getManualDateTimeEditVersion,
  setExifGps,
  selectedFile,
  snapshotFile,
  snapshotStatus,
  selectedMediaType,
  imagePreviewUrl,
}) {
  const applySelectedMedia = useCallback(
    async (mediaBundle, type = "image", selectionId) => {
      const nextSnapshotFile = mediaBundle?.snapshotFile || null;
      const nextSnapshotStatus = mediaBundle?.status || "failed";
      const originalSelectedFile = mediaBundle?.originalFile || null;
      const hasSnapshot = Boolean(
        nextSnapshotFile && nextSnapshotStatus === "success"
      );
      const file = hasSnapshot ? nextSnapshotFile : originalSelectedFile;
      const isSelectionCurrent = () => selectionIdRef.current === selectionId;

      if (!file) {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "invariant_upload_source_missing_before_media",
          selectionId,
          snapshotStatus: nextSnapshotStatus,
          hasSnapshotFile: Boolean(nextSnapshotFile),
          hasOriginalFile: Boolean(originalSelectedFile),
        });
        const baseState = {
          selectionId,
          selectedFile: null,
          originalFile: null,
          snapshotFile: null,
          snapshotStatus: "failed",
          uploadFile: null,
          uploadFilename: "",
          uploadMimeType: "",
          mediaType: type,
          previewUrl: "",
          previewSource: "",
          previewStatus: "failed",
          exifStatus: "failed",
          submitReady: false,
          blockingError: "",
          blockingErrorCode: "",
          nonBlockingMessage: "",
        };
        uploadAssetRef.current = {
          selectionId,
          snapshotStatus: "failed",
          originalFile: null,
          snapshotFile: null,
          previewStatus: "failed",
          previewBlobUrl: "",
          exifStatus: "failed",
        };
        setMediaStateForSelection(baseState);
        setSubmitPhase(SUBMIT_PHASES.FAILED, { reason: "media_missing" });
        return;
      }
      if (!isSelectionCurrent()) {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "invariant_stale_selection_for_snapshot",
          selectionId,
          currentSelectionId: selectionIdRef.current ?? null,
        });
        return;
      }

      const normalizedType = inferSelectedMediaType(file, type);
      const previewName = typeof file?.name === "string" ? file.name : "";
      const previewMimeType = typeof file?.type === "string" ? file.type : "";
      const isImage = normalizedType === "image";

      if (isImage && !hasSnapshot) {
        clearDeferredFileInput("invalid_image_upload_source");
        rejectImageSelectionWithoutSnapshot(selectionId, {
          snapshotStatus: nextSnapshotStatus,
          failureCode: mediaBundle?.failureCode || "",
          failureReason: mediaBundle?.failureReason || "",
          message:
            mediaBundle?.failureMessage ||
            CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
          reason: "apply_selected_media_without_snapshot",
        });
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "invalid_image_upload_source_blocked",
          selectionId,
          snapshotStatus: nextSnapshotStatus,
          sourceKind: "original_picker_file",
          reason: "apply_selected_media_without_snapshot",
          hasSnapshotFile: Boolean(nextSnapshotFile),
          hasOriginalFile: Boolean(originalSelectedFile),
        });
        return;
      }

      const sourceKind = hasSnapshot ? "stable_snapshot" : "original_picker_file";
      const directSafeResult = isImage
        ? isDirectCatchImageFormatSafe(file, previewName)
        : true;
      const needsPreparationResult =
        hasSnapshot && isImage && requiresClientImagePreparation(file, previewName);
      const resolvedUploadFilename = buildUploadFilename(
        { file, name: previewName },
        normalizedType,
        normalizeMimeType(previewMimeType)
      );

      setSubmitPhase(SUBMIT_PHASES.PREPARING, { reason: "media_selected" });

      console.log("[CATCH_IMG_DEBUG]", {
        stage: "classify",
        normalizedType,
        isDirectSafe: directSafeResult,
        requiresPreparation: needsPreparationResult,
        normalizedMimeType: normalizeMimeType(file?.type),
        extension: getFilenameExtension(previewName),
        hasSnapshot,
      });

      console.log("[CATCH_IMG_DEBUG]", {
        stage: "upload_source_chosen",
        sourceKind,
        selectionId,
        name: previewName,
        type: previewMimeType,
        snapshotStatus: nextSnapshotStatus,
        snapshotSize: Number.isFinite(nextSnapshotFile?.size)
          ? nextSnapshotFile.size
          : null,
        originalSize: Number.isFinite(originalSelectedFile?.size)
          ? originalSelectedFile.size
          : null,
        downstreamUsesStableSelectedFile: hasSnapshot,
      });

      const baseState = {
        selectionId,
        selectedFile: file,
        originalFile: originalSelectedFile,
        snapshotFile: hasSnapshot ? nextSnapshotFile : null,
        snapshotStatus: hasSnapshot ? "success" : nextSnapshotStatus,
        uploadFile: file,
        uploadFilename: resolvedUploadFilename,
        uploadMimeType: previewMimeType,
        mediaType: normalizedType,
        previewUrl: "",
        previewSource: "",
        previewStatus: "pending",
        exifStatus: isImage && hasSnapshot ? "pending" : "idle",
        submitReady: computeSubmitReady(file, ""),
        blockingError: "",
        blockingErrorCode: "",
        nonBlockingMessage: "",
      };

      uploadAssetRef.current = {
        selectionId,
        snapshotStatus: hasSnapshot ? "success" : nextSnapshotStatus,
        originalFile: originalSelectedFile,
        snapshotFile: hasSnapshot ? nextSnapshotFile : null,
        previewStatus: "pending",
        previewBlobUrl: "",
        exifStatus: isImage && hasSnapshot ? "pending" : "idle",
      };

      setMediaStateForSelection(baseState);
      setExifGps?.(null);
      setSubmitPhase(SUBMIT_PHASES.READY, { reason: "media_ready" });

      const previewInputKind = hasSnapshot ? "snapshot" : "original_file";
      buildPreviewSource(file, normalizedType, {
        selectionId,
        previewInputKind,
      })
        .then((nextPreview) => {
          if (!isSelectionCurrent()) {
            return;
          }
          if (nextPreview.url) {
            updateMediaState(selectionId, (prev) => ({
              ...prev,
              previewUrl: nextPreview.url,
              previewSource: nextPreview.previewSource,
              previewStatus: "ready",
              nonBlockingMessage: "",
            }));
            uploadAssetRef.current = {
              ...uploadAssetRef.current,
              previewStatus: "ready",
              previewBlobUrl: nextPreview.url,
            };
            return;
          }

          if (isImage && nextPreview.previewStatus === "placeholder") {
            updateMediaState(selectionId, (prev) => ({
              ...prev,
              previewSource: "placeholder",
              previewStatus: "placeholder",
              nonBlockingMessage: CATCH_IMAGE_PREVIEW_UNAVAILABLE_MESSAGE,
            }));
            uploadAssetRef.current = {
              ...uploadAssetRef.current,
              previewStatus: "placeholder",
              previewBlobUrl: "",
            };
            return;
          }

          if (isImage) {
            updateMediaState(selectionId, (prev) => ({
              ...prev,
              previewSource: "",
              previewStatus: "failed",
              nonBlockingMessage: CATCH_IMAGE_PREVIEW_UNAVAILABLE_MESSAGE,
            }));
            uploadAssetRef.current = {
              ...uploadAssetRef.current,
              previewStatus: "failed",
              previewBlobUrl: "",
            };
          }
        })
        .catch((error) => {
          if (!isSelectionCurrent()) {
            return;
          }
          if (isImage) {
            updateMediaState(selectionId, (prev) => ({
              ...prev,
              previewSource: "placeholder",
              previewStatus: "placeholder",
              nonBlockingMessage: CATCH_IMAGE_PREVIEW_UNAVAILABLE_MESSAGE,
            }));
            uploadAssetRef.current = {
              ...uploadAssetRef.current,
              previewStatus: "placeholder",
              previewBlobUrl: "",
            };
          }
          console.warn("[CATCH_IMG_DEBUG]", {
            stage: "preview_generation_failed_nonblocking",
            selectionId,
            mediaType: normalizedType,
            previewInputKind,
            errorName: error?.name,
            errorMessage: error?.message,
          });
        });

      if (needsPreparationResult) {
        normalizeImageFileForCatchMedia(file, { filename: previewName })
          .then((normalizedImage) => {
            if (!isSelectionCurrent()) {
              return;
            }
            const normalizedFile = normalizedImage.file || null;
            const normalizedFilename = buildUploadFilename(
              { file: normalizedFile, name: normalizedImage.filename },
              normalizedType,
              normalizeMimeType(normalizedImage.mimeType)
            );
            if (!normalizedFile) {
              throw new Error("Missing normalized image");
            }
            uploadAssetRef.current = {
              ...uploadAssetRef.current,
              snapshotFile: normalizedFile,
              snapshotStatus: "success",
            };
            updateMediaState(selectionId, (prev) => ({
              ...prev,
              selectedFile: normalizedFile,
              snapshotFile: normalizedFile,
              snapshotStatus: "success",
              uploadFile: normalizedFile,
              uploadFilename: normalizedFilename,
              uploadMimeType: normalizedImage.mimeType,
              submitReady: computeSubmitReady(normalizedFile, ""),
              blockingError: "",
              blockingErrorCode: "",
            }));
            setSubmitPhase(SUBMIT_PHASES.READY, { reason: "media_ready" });
          })
          .catch((error) => {
            if (!isSelectionCurrent()) {
              return;
            }
            console.error("[CATCH_IMG_DEBUG]", {
              stage: "normalizationError",
              scope: "applySelectedMedia",
              errorName: error?.name,
              errorMessage: error?.message,
              causeName: error?.cause?.name,
              causeMessage: error?.cause?.message,
              fallbackSourceKind: sourceKind,
            });
            updateMediaState(selectionId, (prev) => ({
              ...prev,
              blockingError: "",
              blockingErrorCode: "",
              submitReady: computeSubmitReady(prev.uploadFile, ""),
            }));
            setSubmitPhase(SUBMIT_PHASES.READY, { reason: "media_ready" });
          });
      }

      if (!isImage) {
        const manualDateTimeEditVersion =
          typeof getManualDateTimeEditVersion === "function"
            ? getManualDateTimeEditVersion()
            : 0;

        inspectVideoPreviewMetadata(file)
          .then((videoPreviewMetadata) => {
            if (!isSelectionCurrent()) {
              return;
            }
            if (
              typeof getManualDateTimeEditVersion === "function" &&
              getManualDateTimeEditVersion() !== manualDateTimeEditVersion
            ) {
              return;
            }

            const capturedAtParts = extractVideoCapturedAtPreviewParts(
              videoPreviewMetadata?.capturedAt
            );
            if (!capturedAtParts) {
              return;
            }

            if (capturedAtParts.date) {
              applyDetectedExifDate?.(capturedAtParts.date);
            }

            if (capturedAtParts.time) {
              applyDetectedExifTime?.(capturedAtParts.time);
            }
          })
          .catch((error) => {
            if (!isSelectionCurrent()) {
              return;
            }
            console.warn("[CATCH_VIDEO_DEBUG]", {
              stage: "video_preview_metadata_inspect_failed",
              selectionId,
              errorName: typeof error?.name === "string" ? error.name : "",
              errorMessage:
                typeof error?.message === "string" ? error.message : "",
            });
          });

        updateMediaState(selectionId, (prev) => ({
          ...prev,
          exifStatus: "idle",
        }));
        uploadAssetRef.current = {
          ...uploadAssetRef.current,
          exifStatus: "idle",
        };
        return;
      }

      if (!hasSnapshot) {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "exif_skipped_without_snapshot",
          selectionId,
          snapshotStatus: nextSnapshotStatus,
          hasOriginalFile: Boolean(originalSelectedFile),
        });
        return;
      }

      if (uploadAssetRef.current.snapshotFile !== file) {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "invariant_exif_source_mismatch",
          selectionId,
          snapshotStatus: nextSnapshotStatus,
        });
      }

      extractExifData(file)
        .then((exif) => {
          if (!isSelectionCurrent()) {
            return;
          }

          updateMediaState(selectionId, (prev) => ({
            ...prev,
            exifStatus: "ready",
          }));
          uploadAssetRef.current = {
            ...uploadAssetRef.current,
            exifStatus: "ready",
          };

          if (exif?.datetimeOriginal) {
            const [datePart, timePart] = exif.datetimeOriginal.split("T");

            if (datePart) {
              applyDetectedExifDate?.(datePart);
            }

            if (timePart) {
              applyDetectedExifTime?.(timePart.slice(0, 5));
            }
          }

          // GPS auto-fill runs on the eager-read path in
          // useCatchMediaSelectionFlow, which consumes the stabilized
          // ArrayBuffer directly. Intentionally no setExifGps(exif.gps) here.
        })
        .catch((error) => {
          if (!isSelectionCurrent()) {
            return;
          }
          updateMediaState(selectionId, (prev) => ({
            ...prev,
            exifStatus: "failed",
          }));
          uploadAssetRef.current = {
            ...uploadAssetRef.current,
            exifStatus: "failed",
          };
          console.warn("[CATCH_IMG_DEBUG]", {
            stage: "exif_apply_flow_extract_failed",
            selectionId,
            errorName: typeof error?.name === "string" ? error.name : "",
            errorMessage:
              typeof error?.message === "string" ? error.message : "",
          });
        });
    },
    [
      applyDetectedExifDate,
      applyDetectedExifTime,
      clearDeferredFileInput,
      getManualDateTimeEditVersion,
      rejectImageSelectionWithoutSnapshot,
      selectionIdRef,
      setExifGps,
      setMediaStateForSelection,
      setSubmitPhase,
      updateMediaState,
      uploadAssetRef,
    ]
  );

  const handleImagePreviewError = useCallback(async () => {
    const previewFile = snapshotFile || selectedFile;
    if (!previewFile || selectedMediaType !== "image") {
      return;
    }
    if (snapshotStatus !== "success") {
      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "preview_without_snapshot",
        selectionId: selectionIdRef.current,
        snapshotStatus,
      });
      const selectionId = selectionIdRef.current;
      updateMediaState(selectionId, (prev) => ({
        ...prev,
        previewUrl: "",
        previewSource: "placeholder",
        previewStatus: "placeholder",
        nonBlockingMessage: CATCH_IMAGE_PREVIEW_UNAVAILABLE_MESSAGE,
      }));
      uploadAssetRef.current = {
        ...uploadAssetRef.current,
        previewStatus: "placeholder",
        previewBlobUrl: "",
      };
      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "preview_placeholder_used",
        selectionId,
        mediaType: selectedMediaType,
        previewInputKind: "original_file",
        reason: "preview_without_snapshot",
      });
      return;
    }

    const selectionId = selectionIdRef.current;
    const currentUrlKind =
      typeof imagePreviewUrl === "string"
        ? imagePreviewUrl.startsWith("data:")
          ? "data:"
          : imagePreviewUrl.startsWith("blob:")
            ? "blob:"
            : "other"
        : "none";

    if (typeof imagePreviewUrl === "string" && imagePreviewUrl.startsWith("data:")) {
      if (selectionIdRef.current !== selectionId) {
        return;
      }
      updateMediaState(selectionId, (prev) => ({
        ...prev,
        previewUrl: "",
        previewSource: "placeholder",
        previewStatus: "placeholder",
        nonBlockingMessage: CATCH_IMAGE_PREVIEW_UNAVAILABLE_MESSAGE,
      }));
      uploadAssetRef.current = {
        ...uploadAssetRef.current,
        previewStatus: "placeholder",
        previewBlobUrl: "",
      };
      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "preview_placeholder_used",
        selectionId,
        mediaType: selectedMediaType,
        previewInputKind: "snapshot",
        reason: "data_url_failed",
      });
      return;
    }

    try {
      const fallbackUrl = await readFileAsDataUrl(previewFile);
      if (!fallbackUrl) {
        throw buildCatchMediaClientError(
          CATCH_IMAGE_PREVIEW_ERROR_MESSAGE,
          CATCH_IMAGE_PREVIEW_ERROR_CODE
        );
      }

      if (selectionIdRef.current !== selectionId) {
        return;
      }
      updateMediaState(selectionId, (prev) => ({
        ...prev,
        previewUrl: fallbackUrl,
        previewSource: "data-url",
        previewStatus: "ready",
        nonBlockingMessage: "",
      }));
      uploadAssetRef.current = {
        ...uploadAssetRef.current,
        previewStatus: "ready",
        previewBlobUrl: fallbackUrl,
      };
      console.log("[CATCH_IMG_DEBUG]", {
        stage: "preview_generated",
        selectionId,
        mediaType: selectedMediaType,
        previewInputKind: "snapshot",
        previewSource: "data-url",
        urlKind: "data-url",
      });
    } catch (error) {
      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "preview_generation_failed_nonblocking",
        currentUrlKind,
        errorName: error?.name,
        errorMessage: error?.message,
        selectionId,
        mediaType: selectedMediaType,
        previewInputKind: "snapshot",
      });
      if (selectionIdRef.current !== selectionId) {
        return;
      }
      updateMediaState(selectionId, (prev) => ({
        ...prev,
        previewUrl: "",
        previewSource: "placeholder",
        previewStatus: "placeholder",
        nonBlockingMessage: CATCH_IMAGE_PREVIEW_UNAVAILABLE_MESSAGE,
      }));
      uploadAssetRef.current = {
        ...uploadAssetRef.current,
        previewStatus: "placeholder",
        previewBlobUrl: "",
      };
      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "preview_placeholder_used",
        selectionId,
        mediaType: selectedMediaType,
        previewInputKind: "snapshot",
        reason: "fallback_failed",
      });
    }
  }, [
    imagePreviewUrl,
    selectedFile,
    selectedMediaType,
    selectionIdRef,
    snapshotFile,
    snapshotStatus,
    updateMediaState,
    uploadAssetRef,
  ]);

  return {
    applySelectedMedia,
    handleImagePreviewError,
  };
}
